import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { getLocaleInURLFormat, isNumber } from 'utils';

import { withSeoData } from 'hocs';

import PageContentSection from 'components/ContentSection';
import Table from 'components/ConvertorTable';
import CopyButton from 'components/CopyButton';
import ContentPlaceholder from 'components/ContentPlaceholder';
import { SectionTitle } from 'components/SectionTitle';

import { BankAmount, RatesCeil } from './styles';

class AllRatesTable extends PureComponent {
  columnsIds = ['bank', 'rate', 'amount'];

  getTextForCopy = ({ id, summ, timestamp }) => {
    const { sourceValue, sourceType, intl, source, target } = this.props;

    const formattedConverterType = intl.formatMessage(
      {
        id: `allRates.${id}.row.title`,
      },
      { case: 1 }
    );

    const sourceLabel = intl.formatMessage({
      id: `tables.source.label`,
    });

    const sourceLink = `https://${process.env.APP_DOMAIN}${getLocaleInURLFormat(
      intl.locale
    )}${process.env.APP_ASSETS_PUBLIC_PATH}`;

    const formattedSourceValue = intl.formatNumber(sourceValue, {
      maximumFractionDigits: sourceType === 'crypto' ? 8 : 2,
    });

    return `${formattedSourceValue} ${this.getCurrencyNameForCopy(
      source
    )} = ${summ} ${this.getCurrencyNameForCopy(
      target
    )} ${formattedConverterType} за ${timestamp}. ${sourceLabel}: ${sourceLink}`;
  };

  getCurrencyNameForCopy = ({ nameGp, name, code }) => {
    return nameGp || (name && name.toLowerCase()) || code;
  };

  getFormattedSourceValue = () => {
    const { sourceValue, sourceType, intl } = this.props;

    return intl.formatNumber(sourceValue, {
      maximumFractionDigits: sourceType === 'crypto' ? 8 : 2,
    });
  };

  getColumnsConfig = () => {
    const { intl, target, source } = this.props;

    const formattedSourceValue = this.getFormattedSourceValue();

    return this.columnsIds.map(id => {
      if (id === 'amount') {
        return {
          id,
          children: `${formattedSourceValue} ${source.code} в ${target.code}`,
        };
      }

      return {
        id,
        children:
          id !== 'bank'
            ? intl.formatMessage(
                {
                  id: `allBanks.${id}.col.title`,
                },
                { currency: target.code }
              )
            : null,
      };
    });
  };

  getRowsConfig = () => {
    const { rates, target, ratesIsFetching, intl } = this.props;

    return [...rates]
      .sort(({ rate: rateA }, { rate: rateB }) => {
        if (rateA === '-') {
          return 1;
        }

        if (rateB === '-') {
          return -1;
        }

        return rateA - rateB;
      })
      .map(({ id, rate, summ, type, timestamp }) => {
        const roundedSumm = isNumber(summ)
          ? intl.formatNumber(summ, {
              maximumFractionDigits: type === 'crypto' ? 8 : 2,
            })
          : '-';

        return {
          id,
          ceils: [
            {
              key: `rateSource-${id}`,
              children: intl.formatMessage(
                {
                  id: `allRates.${id}.row.title`,
                },
                { case: 0 }
              ),
              props: {
                style: {
                  width: '40%',
                  textAlign: 'left',
                },
              },
            },
            {
              render: () => (
                <RatesCeil key={`rate-${id}`}>
                  {!ratesIsFetching ? (
                    <>{isNumber(rate) ? intl.formatNumber(rate) : '-'}</>
                  ) : (
                    <ContentPlaceholder
                      style={{ width: '58px', height: '20px' }}
                    />
                  )}
                </RatesCeil>
              ),
            },
            {
              key: `amount-${id}`,
              children: !ratesIsFetching ? (
                <BankAmount>
                  {isNumber(summ) ? (
                    <CopyButton
                      textForCopy={this.getTextForCopy({
                        id,
                        summ: roundedSumm,
                        timestamp,
                      })}
                      summ={roundedSumm}
                      currency={target.code}
                    />
                  ) : (
                    '-'
                  )}
                </BankAmount>
              ) : (
                <ContentPlaceholder
                  style={{ width: '146px', height: '20px' }}
                />
              ),
            },
          ],
        };
      });
  };

  render() {
    const { allRatesTitle } = this.props;

    const columns = this.getColumnsConfig();
    const rows = this.getRowsConfig();

    return (
      <PageContentSection as="section">
        <SectionTitle>{allRatesTitle}</SectionTitle>
        <Table columns={columns} rows={rows} />
      </PageContentSection>
    );
  }
}

export default withSeoData(AllRatesTable);

AllRatesTable.propTypes = {
  rates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      summ: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  sourceValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  sourceType: PropTypes.string.isRequired,
  source: PropTypes.objectOf(PropTypes.string).isRequired,
  target: PropTypes.objectOf(PropTypes.string).isRequired,
  ratesIsFetching: PropTypes.bool.isRequired,

  intl: PropTypes.shape({
    locale: PropTypes.string,
    formatMessage: PropTypes.func,
    formatNumber: PropTypes.func,
  }).isRequired,

  allRatesTitle: PropTypes.string.isRequired,
};
